import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Code, Cpu, Wrench, Car, Monitor, GitBranch, Zap, Bot } from 'lucide-react';
import SectionHeader from '@/components/shared/SectionHeader';
import { skills } from '@/data/portfolioConfig';

const categoryIcons: Record<string, React.ElementType> = {
  aiGenAI: Bot,
  programming: Code,
  roboticsAutomation: Cpu,
  toolsFrameworks: Wrench,
  automotive: Car,
  operatingSystems: Monitor,
  methodologies: GitBranch
};


export default function SkillsNew() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'it' | 'en';

  const skillCategories = [
    { key: 'aiGenAI', data: skills.aiGenAI },
    { key: 'roboticsAutomation', data: skills.roboticsAutomation },
    { key: 'programming', data: skills.programming },
    { key: 'automotive', data: skills.automotive },
    { key: 'toolsFrameworks', data: skills.toolsFrameworks },
    { key: 'operatingSystems', data: skills.operatingSystems },
    { key: 'methodologies', data: skills.methodologies }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 15
      }
    }
  };

  return (
    <section id="skills" className="section bg-surface dark:bg-dark-bg-subtle border-b-2 border-border-subtle dark:border-border-dark">
      <div className="max-w-[1600px] mx-auto px-4">
        <SectionHeader
          icon={Zap}
          tag={t('skills.tag', 'Stack')}
          index={4}
          title={t('skills.title')}
          subtitle={t('skills.subtitle')}
        />

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="space-y-6 sm:space-y-8 md:space-y-12"
        >
          {skillCategories.map(({ key, data }) => {
            const Icon = categoryIcons[key] || Code;
            return (
              <motion.div
                key={key}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="bg-surface dark:bg-dark-bg-elevated rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border-2 border-border dark:border-primary/20 shadow-xl dark:shadow-primary/10 hover:shadow-2xl hover:shadow-primary/20 transition-shadow duration-300"
              >
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <div className="p-2 sm:p-3 bg-gradient-to-br from-primary/30 to-primary/20 rounded-lg sm:rounded-xl">
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-text-primary dark:text-light-text">
                    {data.title[currentLang]}
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {data.items.map((skill: any, index: number) => {
                    const skillName = typeof skill.name === 'object' ? skill.name[currentLang] : skill.name;
                    const skillDesc = typeof skill.description === 'object'
                      ? skill.description[currentLang]
                      : skill.description;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05, type: 'spring', stiffness: 150 }}
                        whileHover={{ scale: 1.08, y: -8, rotateZ: 1 }}
                        whileTap={{ scale: 0.95 }}
                        className="group relative bg-surface dark:bg-surface-dark rounded-lg sm:rounded-xl p-3 sm:p-4 border-2 border-border dark:border-primary/20 hover:border-primary dark:hover:border-primary/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/30 dark:hover:shadow-primary/40 cursor-pointer"
                      >
                        <h4 className="font-bold text-sm sm:text-base text-text-primary dark:text-surface mb-1 sm:mb-2 pr-6">
                          {skillName}
                        </h4>

                        {skillDesc && (
                          <p className="text-xs sm:text-sm text-text-secondary dark:text-text-light">
                            {skillDesc}
                          </p>
                        )}

                        {/* Decorative gradient on hover */}
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 dark:via-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

      </div>
    </section>
  );
}
